<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from '../helpers/use-locale';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faGrid2, faCheck, faList } from '@fortawesome/pro-solid-svg-icons';

library.add([faGrid2, faCheck, faList]);

type Emits = {
  (e: 'toggleViewType'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  viewType: 'list' | 'grid';
  viewTypeOptions?: ('list' | 'grid')[];
  rounded?: 'sm' | 'default' | 'lg';
  density?: 'default' | 'compact';
  collapse?: boolean;
  bordered?: boolean;
  size?: 'x-small' | 'small' | 'default';
};
const props = withDefaults(defineProps<Props>(), {
  rounded: 'lg',
  density: 'default',
  collapse: false,
  bordered: false,
});
const { t } = useLocale();
const { smAndDown, xs, platform } = useDisplay();

const buttonSize = computed(() => props.size ?? (xs.value ? 'small' : 'default'));

const isSingleToggle = computed(() => smAndDown.value || props.collapse);

const singleToggleProps = computed(() =>
  props.bordered
    ? ({
        variant: 'flat',
        color: 'surface-container',
        rounded: props.rounded === 'default' ? undefined : props.rounded,
        ripple: false,
        class: 'select-btn select-btn--square',
      } as const)
    : ({ variant: 'text', color: 'primary', class: 'text-inverse-surface' } as const),
);

const isTouchDevice = computed(() => platform.value.touch);

const toggleLabel = computed(() =>
  t(props.viewType === 'grid' ? 'components.label_switch_to_list_view' : 'components.label_switch_to_grid_view'),
);
</script>

<template>
  <div class="d-flex align-center mb-2 mb-sm-5" :class="{ compact: density === 'compact' }">
    <slot name="top"></slot>
    <template v-if="!viewTypeOptions || viewTypeOptions.length > 1">
      <v-btn
        v-if="isSingleToggle"
        v-bind="singleToggleProps"
        :size="buttonSize"
        class="ml-3"
        :aria-label="toggleLabel"
        @click="emit('toggleViewType')"
      >
        <FontAwesomeIcon :icon="viewType === 'grid' ? 'fas fa-list' : 'fas fa-grid-2'" />
        <v-tooltip :disabled="isTouchDevice" activator="parent" :text="toggleLabel" location="bottom" />
      </v-btn>
      <div v-if="!isSingleToggle" class="view-buttons ml-3">
        <v-btn
          variant="outlined"
          class="rounded-e-0 list-btn text-none text-inverse-surface"
          color=""
          :class="{
            'rounded-s-pill': rounded === 'lg',
            'rounded-s-sm': rounded === 'sm',
            active: viewType === 'list',
          }"
          :aria-label="t('components.label_list_view')"
          @click="emit('toggleViewType')"
        >
          <template #prepend>
            <FontAwesomeIcon :icon="viewType === 'list' ? 'fas fa-check' : 'fas fa-list'" />
          </template>
          {{ t('components.label_list_view') }}
        </v-btn>
        <v-btn
          variant="outlined"
          class="rounded-s-0 text-none text-inverse-surface"
          :class="{
            'rounded-e-pill': rounded === 'lg',
            'rounded-e-sm': rounded === 'sm',
            active: viewType === 'grid',
          }"
          :aria-label="t('components.label_grid_view')"
          @click="emit('toggleViewType')"
        >
          <template #prepend>
            <FontAwesomeIcon :icon="viewType === 'grid' ? 'fas fa-check' : 'fas fa-grid-2'" />
          </template>
          {{ t('components.label_grid_view') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.select-btn--square {
  min-width: 0;
  width: var(--v-btn-height);
  padding: 0;
}

.select-btn {
  border: 1px solid rgb(var(--v-theme-outline-variant));

  &:hover {
    --v-hover-opacity: 0;
    --v-activated-opacity: 0;
  }
}

.view-buttons {
  .v-btn {
    border: 1px solid rgb(var(--v-theme-outline-variant));
    height: 48px;
    background-color: rgb(var(--v-theme-surface-container));
  }

  .list-btn {
    border-right: none;
  }

  .active.v-btn {
    background-color: rgb(var(--v-theme-secondary-container));
    color: rgb(var(--v-theme-on-secondary-container)) !important;
    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }
}

.compact .view-buttons .v-btn {
  height: 36px;
}
</style>
